import React, { FC } from 'react';

import { Button } from 'ncoded-component-library';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import classNames from 'classnames';
import useQueryParams from 'hooks/useQueryParams';
import { PaginationInfo } from 'models/PaginatedData';

import './Pagination.styles.scss';

type PaginationProps = {
  pagination: PaginationInfo;
};

const Pagination: FC<PaginationProps> = ({ pagination }) => {
  const { currentPage, totalPages } = pagination;
  const { setMultipleQueryParams } = useQueryParams();

  const handlePageChange = (page: number) => {
    setMultipleQueryParams({ page });
  };

  const getPageNumbers = () => {
    const pages: (number | '...')[] = [];
    const edgePages = 3; // how many pages to always show before/after the window
    const windowSize = 1; // how many pages to show on each side of currentPage

    // if it's small, just show everything
    if (totalPages <= edgePages * 2 + windowSize * 2 + 1) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // **Start block**: only when currentPage is among the first 3
    if (currentPage <= edgePages) {
      // show 1 through (edgePages + windowSize), then “…”, then last
      for (let i = 1; i <= edgePages + windowSize; i++) {
        pages.push(i);
      }
      pages.push('...', totalPages);
      return pages;
    }

    // **End block**: only when currentPage is among the last 3
    if (currentPage > totalPages - edgePages) {
      // show first, “…”, then (last edgePages+windowSize) pages
      pages.push(1, '...');
      for (
        let i = totalPages - (edgePages + windowSize) + 1;
        i <= totalPages;
        i++
      ) {
        pages.push(i);
      }
      return pages;
    }

    // **Middle block**: sliding window of 1 around currentPage
    pages.push(
      1,
      '...',
      currentPage - windowSize,
      currentPage,
      currentPage + windowSize,
      '...',
      totalPages,
    );
    return pages;
  };

  if (totalPages < 2) return null;

  return (
    <div className="pagination">
      <div className="controls">
        <Button
          variant="icon"
          className="nav-btn"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <FiChevronLeft />
          Previous
        </Button>

        <div className="page-numbers">
          {getPageNumbers().map((page, index) =>
            page === '...' ? (
              <span key={index} className="dots">
                ...
              </span>
            ) : (
              <Button
                key={index}
                variant="icon"
                className={classNames('page-btn', {
                  'page-btn--active': page === currentPage,
                })}
                onClick={() => handlePageChange(page as number)}
              >
                {page}
              </Button>
            ),
          )}
        </div>

        <Button
          variant="icon"
          className="nav-btn"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
          <FiChevronRight />
        </Button>
      </div>
    </div>
  );
};

export default Pagination;
